/**
 * Axios response-error handler for JWT 401s.
 * Unrecoverable expiry clears the session and does not reject, so callers
 * (and CRA's overlay) never see an uncaught AxiosError.
 */

export function createPendingAuthRedirect() {
  return new Promise(() => {});
}

export function createAuthResponseErrorHandler({
  getRefreshToken,
  refreshAccessToken,
  getIsRefreshing,
  setIsRefreshing,
  subscribeTokenRefresh,
  onRefreshed,
  clearSessionFromInterceptor,
  retryRequest,
  isAuthExemptRequestUrl,
}) {
  return async function handleAuthResponseError(error) {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const requestUrl = originalRequest.url || originalRequest.baseURL || '';

    if (status === 401 && isAuthExemptRequestUrl(requestUrl)) {
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refresh = getRefreshToken();
      if (!refresh) {
        const cleared = clearSessionFromInterceptor(originalRequest);
        if (cleared) return createPendingAuthRedirect();
        return Promise.reject(error);
      }

      try {
        if (getIsRefreshing()) {
          const newToken = await new Promise((resolve, reject) => {
            subscribeTokenRefresh((token) => {
              if (token) resolve(token);
              else reject(new Error('Refresh failed'));
            });
          });
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return retryRequest(originalRequest);
        }

        setIsRefreshing(true);
        const newToken = await refreshAccessToken();
        setIsRefreshing(false);
        onRefreshed(newToken);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return retryRequest(originalRequest);
      } catch (refreshErr) {
        setIsRefreshing(false);
        onRefreshed(null);
        const cleared = clearSessionFromInterceptor(originalRequest);
        if (cleared) return createPendingAuthRedirect();
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  };
}
